import {
	truckPlanFieldsToFilters,
	DEFAULT_HARD_FILTERS_VALUES,
	DEFAULT_SOFT_FILTERS_VALUES,
	driverPreferencesToFilters,
} from "../utils/filtersUtils";

export const convertPlanAndTruckToSearch = (carrier, truck, plan, driver, trailer) => {
	const filters = plan
		? truckPlanFieldsToFilters(plan)
		: { hardFilters: DEFAULT_HARD_FILTERS_VALUES, softFilters: DEFAULT_SOFT_FILTERS_VALUES };
	const driverFilters = driverPreferencesToFilters(driver);

	return {
		EMPTY: {
			filters: {
				...filters.hardFilters,
				...{
					carrier: carrier?._id,
					carrier__view: carrier?.carrier__view
						? carrier.carrier__view
						: {
								value: carrier?._id,
								label: carrier?.carrier_company_name,
								description: "MC " + (carrier?.carrier_mc_number ?? " Empty"),
						  },
					truck: truck?._id,
					truck__view: truck?.truck__view
						? truck.truck__view
						: {
								value: truck?._id,
								label: truck?._id,
								description: truck ? `${truck?.trailer}' | ${truck?.max_weight} lb` : "",
						  },
					location_origin: plan?.current_location,
					location_origin__view: plan?.current_location__view
						? plan?.current_location__view
						: { label: plan?.current_location, value: plan?.current_location },
					equipment: truck?.subtype ? "SPECIAL_" + truck.subtype : truck?.equipment,
					max_weight: filters?.hardFilters?.max_weight ?? trailer?.max_weight,
					load_type: trailer?.load_type ?? filters?.hardFilters?.load_type,
					trailer: filters?.hardFilters?.trailer ?? trailer?.trailer,
				},
				...driverFilters,
			},
			softFilters: filters?.softFilters,
		},
	};
};

/** Equipment for search filters — matches TruckCellView (`subtype ?? equipment`) with VAN default. */
export const resolveTruckSearchEquipment = (truck, truckView) => {
	const subtype = truck?.subtype ?? truckView?.metadata?.subtype;
	const equipment = truck?.equipment ?? truckView?.metadata?.equipment;
	if (subtype) return "SPECIAL_" + subtype;
	if (equipment) return equipment;
	return "VAN";
};

export const convertRelayScheduleChainToSearch = (chain) => {
	const moment = require("moment");

	// Extract origins from pickup and longhaul schedules
	const origins = [];
	const coverageOrigins = [];
	[...(chain.pickup_schedules || [])].forEach((schedule) => {
		if (schedule.origin_yard) {
			if (schedule.origin_yard.search_city && !schedule.was_adjusted && !schedule.covered_trips?.length > 0) {
				origins.push(`${schedule.origin_yard.search_city}, ${schedule.origin_yard.search_state}`);
			} else {
				origins.push(`${schedule.origin_yard.city}, ${schedule.origin_yard.state}`);
			}
			coverageOrigins.push(schedule.origin_yard?.coverageRadius ?? 100);
		}
	});

	// Extract destinations from longhaul and delivery schedules
	const destinations = [];
	const coverageDestinations = [];
	[...(chain.delivery_schedules || [])].forEach((schedule) => {
		if (schedule.destination_yard) {
			if (schedule.destination_yard.search_city && !schedule.was_adjusted && !schedule.covered_trips?.length > 0) {
				destinations.push(`${schedule.destination_yard.search_city}, ${schedule.destination_yard.search_state}`);
			} else {
				destinations.push(`${schedule.destination_yard.city}, ${schedule.destination_yard.state}`);
			}
			coverageDestinations.push(schedule.destination_yard?.coverageRadius ?? 100);
		}
	});

	// Get unique origins and destinations
	const uniqueOrigins = [...new Set(origins)];
	const uniqueDestinations = [...new Set(destinations)];

	const maxCoverageOrigin = Math.max(...coverageOrigins);
	const maxCoverageDestination = Math.max(...coverageDestinations);

	// Find the earliest pickup schedule start time
	let earliestPickup = null;
	let latestPickup = null;
	if (chain.pickup_schedules && chain.pickup_schedules.length > 0) {
		chain.pickup_schedules.forEach((schedule) => {
			const startTime = moment(schedule.start_datetime);
			const endTime = moment(schedule.end_datetime);
			if (!earliestPickup || startTime.isBefore(earliestPickup)) {
				earliestPickup = startTime;
			}
			if (!latestPickup || endTime.isAfter(latestPickup)) {
				latestPickup = endTime;
			}
		});
	}

	earliestPickup = moment(earliestPickup).startOf("day").format("YYYY-MM-DD");
	latestPickup = moment.utc(earliestPickup).endOf("day");

	const { hardFilters, softFilters } = {
		hardFilters: { ...DEFAULT_HARD_FILTERS_VALUES },
		softFilters: { ...DEFAULT_SOFT_FILTERS_VALUES },
	};

	// Add driver filters if driver information is available
	const driverFilters = chain.driver ? driverPreferencesToFilters(chain.driver) : {};

	// Add delivery date range if we have delivery schedules
	let latestDelivery = null;
	let earliestDelivery = null;
	if (chain.delivery_schedules && chain.delivery_schedules.length > 0) {
		chain.delivery_schedules.forEach((schedule) => {
			const endTime = moment(schedule.end_datetime);
			const startTime = moment(schedule.start_datetime);
			if (!latestDelivery || endTime.isAfter(latestDelivery)) {
				latestDelivery = endTime;
			}
			if (!earliestDelivery || startTime.isBefore(earliestDelivery)) {
				earliestDelivery = startTime;
			}
		});
	}

	// Add carrier and truck filters from the chain item
	const finalFilters = {
		...hardFilters,
		location_origin: uniqueOrigins[0],
		location_origin__view: {
			value: uniqueOrigins[0],
			label: uniqueOrigins[0],
		},
		dh_origin: maxCoverageOrigin,
		location_destination: uniqueDestinations[0],
		location_destination__view: {
			value: uniqueDestinations[0],
			label: uniqueDestinations[0],
		},
		dh_destination: maxCoverageDestination,
		// Add carrier information if available
		...((chain.carrier || chain.carrier_id) && {
			carrier: chain.carrier || chain.carrier_id,
			carrier__view: chain.carrier__view || {
				value: chain.carrier?._id || chain.carrier_id,
				label: chain.carrier?.carrier_company_name || chain.carrier_name || "Unknown Carrier",
				description: "MC " + (chain.carrier?.carrier_mc_number || chain.carrier_mc_number || " Empty"),
			},
		}),
		// Add truck information if available
		...((chain.truck || chain.truck_id) && {
			truck: chain.truck?._id || chain.truck_id,
			truck__view: chain.truck__view || {
				value: chain.truck?._id || chain.truck_id,
				label: chain.truck?._id || chain.truck_id,
				description: chain.truck ? `${chain.truck?.truck_trailer__view?.label}` : "",
			},
		}),
		equipment: resolveTruckSearchEquipment(chain.truck, chain.truck__view),
		// Add truck specifications with fallbacks
		max_weight: chain.truck__view?.metadata?.max_weight,
		trailer: chain.truck__view?.metadata?.trailer,
		// Add pickup date filters if we have pickup schedules
		...(earliestPickup &&
			latestPickup && {
				pickup_ds__after: earliestPickup.valueOf(),
				pickup_ds__before: latestPickup.valueOf(),
			}),
		// Add delivery date filters if we have delivery schedules
		...(latestDelivery &&
			earliestDelivery && {
				delivery_ds__after: moment(latestDelivery).startOf("day").valueOf(),
				delivery_ds__before: moment(earliestDelivery).endOf("day").valueOf(),
			}),
		// Include driver filters
		...driverFilters,
	};

	return {
		EMPTY: {
			filters: finalFilters,
			softFilters: softFilters,
		},
	};
};

// Strictly parse a Live Queue row's chain day to the start of that day.
const liveQueueRowDayStart = (row) => {
	const moment = require("moment");
	if (!row?.date) return null;
	const m = moment(row.date, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm", moment.ISO_8601], true);
	return m.isValid() ? m.clone().startOf("day") : null;
};

/**
 * Resolve the Live Queue Pickup Date moment for a row (when the truck becomes free).
 * Single source of truth shared with the Pickup Date list column: backend-computed
 * `search_pickup_ts`, then the ISO date field, then the anchor delivery on the row day,
 * then the earliest schedule start.
 */
export const resolveLiveQueuePickupMoment = (row) => {
	const moment = require("moment");
	if (row?.search_pickup_ts != null) {
		const m = moment(row.search_pickup_ts);
		if (m.isValid()) return m;
	}
	if (row?.search_pickup_date) {
		const m = moment(row.search_pickup_date);
		if (m.isValid()) return m;
	}
	const day = liveQueueRowDayStart(row);
	if (!day) return null;
	let bestDelivery = null;
	(row.covering_trips || []).forEach((trip) => {
		const d = moment(trip.delivery_date || trip.delivery_window_date);
		if (!d.isValid() || !d.isSame(day, "day")) return;
		if (!bestDelivery || d.isAfter(bestDelivery)) bestDelivery = d;
	});
	if (bestDelivery) return bestDelivery;
	let earliestStart = null;
	[...(row.pickup_schedules || []), ...(row.delivery_schedules || [])].forEach((schedule) => {
		const m = moment(schedule.start_datetime, ["YYYY-MM-DD HH:mm", moment.ISO_8601]);
		if (!m.isValid()) return;
		if (!earliestStart || m.isBefore(earliestStart)) earliestStart = m;
	});
	return earliestStart || day;
};

/**
 * Resolve the Live Queue Delivery Date moment for a row (the next commitment deadline).
 * Single source of truth shared with the Delivery Date list column: backend-computed
 * `search_delivery_ts`, then the ISO date field, then the earliest pickup on the row day
 * after the anchor, then the latest schedule end.
 */
export const resolveLiveQueueDeliveryMoment = (row) => {
	const moment = require("moment");
	if (row?.search_delivery_ts != null) {
		const m = moment(row.search_delivery_ts);
		if (m.isValid()) return m;
	}
	if (row?.search_delivery_date) {
		const m = moment(row.search_delivery_date);
		if (m.isValid()) return m;
	}
	const day = liveQueueRowDayStart(row);
	if (!day) return null;
	const anchor = resolveLiveQueuePickupMoment(row);
	let earliestAfter = null;
	(row.covering_trips || []).forEach((trip) => {
		const p = moment(trip.pickup_date || trip.pickup_window_date);
		if (!p.isValid() || !p.isSame(day, "day")) return;
		if (anchor && !p.isAfter(anchor)) return;
		if (!earliestAfter || p.isBefore(earliestAfter)) earliestAfter = p;
	});
	if (earliestAfter) return earliestAfter;
	let latestEnd = null;
	[...(row.pickup_schedules || []), ...(row.delivery_schedules || [])].forEach((schedule) => {
		const m = moment(schedule.end_datetime, ["YYYY-MM-DD HH:mm", moment.ISO_8601]);
		if (!m.isValid()) return;
		if (!latestEnd || m.isAfter(latestEnd)) latestEnd = m;
	});
	return latestEnd || day.clone().endOf("day");
};

/**
 * Build the Live Queue search date window: pickup window runs from the earliest pickup
 * (truck free) to the latest delivery (next commitment), and the delivery window mirrors
 * it so the backend resolves `availStart`/`deliveryEnd` to the actual gap, not the full day.
 * Returns `{}` when no valid pickup moment can be resolved.
 */
export const buildLiveQueueDateFilters = (row) => {
	const pickup = resolveLiveQueuePickupMoment(row);
	if (!pickup?.isValid()) return {};
	const delivery = resolveLiveQueueDeliveryMoment(row);
	const filters = {
		pickup_ds__after: pickup.valueOf(),
		delivery_ds__after: pickup.valueOf(),
	};
	if (delivery?.isValid()) {
		filters.pickup_ds__before = delivery.valueOf();
		filters.delivery_ds__before = delivery.valueOf();
	}
	return filters;
};

/**
 * Convert a Live Queue row (one truck-day) to search filters. Mirrors
 * `convertRelayScheduleChainToSearch` but consumes the precomputed
 * `row.search_origin` / `row.search_destination` (last trip delivery / next
 * trip pickup, with yard fallbacks) provided by the backend, falling back to
 * the day's pickup/delivery schedule yards when missing.
 */
export const convertLiveQueueRowToSearch = (row) => {
	const pickupSchedule = row?.pickup_schedules?.[0] || null;
	const deliverySchedule = row?.delivery_schedules?.[0] || null;

	// Normalize a free-form address to "CITY, ST" using simple comma parsing.
	// Mirrors lib.parseCitySt on the backend so the search filter's location values stay
	// consistent (the backend already pre-formats trip-derived addresses, but yards / raw
	// fallbacks may still contain street info we want to strip).
	const formatCitySt = (input) => {
		if (!input || typeof input !== "string") return null;
		let cleaned = input.trim().replace(/,?\s*\d{5}(-\d{4})?$/, "");
		cleaned = cleaned.replace(/,?\s*(USA|US|CANADA|CA|MEXICO|MX)\s*$/i, "");
		const parts = cleaned
			.split(",")
			.map((p) => p.trim())
			.filter(Boolean);
		if (parts.length < 2) return null;
		const last = parts[parts.length - 1];
		const lastWords = last.split(/\s+/);
		let city = null;
		let state = null;
		if (lastWords.length >= 2 && /^[A-Z]{2}$/i.test(lastWords[lastWords.length - 1])) {
			state = lastWords[lastWords.length - 1].toUpperCase();
			city = lastWords.slice(0, -1).join(" ") || parts[parts.length - 2];
		} else if (/^[A-Z]{2}$/i.test(last)) {
			state = last.toUpperCase();
			city = parts[parts.length - 2];
		}
		if (!city || !state) return null;
		return `${city.toUpperCase()}, ${state.toUpperCase()}`;
	};

	// Pick the best label for a backend-provided search endpoint: prefer city/state
	// fields (set by the backend via lib.parseCitySt), then sanitize the address itself.
	const endpointToLabel = (endpoint) => {
		if (!endpoint) return null;
		if (endpoint.city && endpoint.state) {
			return `${String(endpoint.city).toUpperCase()}, ${String(endpoint.state).toUpperCase()}`;
		}
		return formatCitySt(endpoint.address) || null;
	};

	// Resolve origin: precomputed search_origin > pickup origin yard > delivery origin yard.
	let originLabel = endpointToLabel(row?.search_origin);
	if (!originLabel) {
		const fallbackYard = pickupSchedule?.origin_yard || deliverySchedule?.origin_yard || null;
		if (fallbackYard) {
			const city = fallbackYard.search_city || fallbackYard.city || null;
			const state = fallbackYard.search_state || fallbackYard.state || null;
			if (city && state) {
				originLabel = `${String(city).toUpperCase()}, ${String(state).toUpperCase()}`;
			}
		}
	}

	// Resolve destination: precomputed search_destination > delivery destination yard > pickup destination yard.
	let destinationLabel = endpointToLabel(row?.search_destination);
	if (!destinationLabel) {
		const fallbackYard = deliverySchedule?.destination_yard || pickupSchedule?.destination_yard || null;
		if (fallbackYard) {
			const city = fallbackYard.search_city || fallbackYard.city || null;
			const state = fallbackYard.search_state || fallbackYard.state || null;
			if (city && state) {
				destinationLabel = `${String(city).toUpperCase()}, ${String(state).toUpperCase()}`;
			}
		}
	}

	// Live-queue searches use FIXED deadhead radii (50 mi origin, 50 mi destination) so the
	// dispatcher gets a tight, predictable load funnel regardless of yard/trip coverageRadius
	// values that may have been configured for other contexts (relay chain searches).
	const LIVE_QUEUE_DH_ORIGIN = 50;
	const LIVE_QUEUE_DH_DESTINATION = 50;

	// Search window: align with the Pickup Date / Delivery Date columns so the search funnels
	// from when the truck becomes free (earliest pickup) until its next commitment (latest
	// delivery), rather than the full calendar day.
	const dateFilters = buildLiveQueueDateFilters(row);

	const { hardFilters, softFilters } = {
		hardFilters: { ...DEFAULT_HARD_FILTERS_VALUES },
		softFilters: { ...DEFAULT_SOFT_FILTERS_VALUES },
	};

	const driverFilters = row?.truck?.driver ? driverPreferencesToFilters(row.truck.driver) : {};

	const finalFilters = {
		...hardFilters,
		...(originLabel && {
			location_origin: originLabel,
			location_origin__view: { value: originLabel, label: originLabel },
			dh_origin: LIVE_QUEUE_DH_ORIGIN,
		}),
		...(destinationLabel && {
			location_destination: destinationLabel,
			location_destination__view: { value: destinationLabel, label: destinationLabel },
			dh_destination: LIVE_QUEUE_DH_DESTINATION,
		}),
		...((row?.carrier || row?.carrier_id) && {
			carrier: row.carrier || row.carrier_id,
			carrier__view: row.carrier__view || {
				value: row.carrier?._id || row.carrier_id,
				label: row.carrier?.carrier_company_name || row.carrier_name || "Unknown Carrier",
				description: "MC " + (row.carrier?.carrier_mc_number || row.carrier_mc_number || " Empty"),
			},
		}),
		...((row?.truck || row?.truck_id) && {
			truck: row.truck?._id || row.truck_id,
			truck__view: row.truck__view || {
				value: row.truck?._id || row.truck_id,
				label: row.truck?.truckid || row.truck?._id || row.truck_id,
				description: row.truck ? `${row.truck?.truck_trailer__view?.label || ""}` : "",
			},
		}),
		equipment: resolveTruckSearchEquipment(row?.truck, row?.truck__view),
		max_weight: row?.truck__view?.metadata?.max_weight,
		trailer: row?.truck__view?.metadata?.trailer,
		...dateFilters,
		...driverFilters,
	};

	return {
		EMPTY: {
			filters: finalFilters,
			softFilters,
		},
	};
};

/**
 * Convert a search request item back to search filters format
 * This allows re-launching a search with the same parameters
 * Handles both backend formats:
 * - Backend format: origin, destination, availStart
 * - UI format: location_origin, location_destination, pickup_ds__after
 */
export const convertSearchRequestToFilters = (searchRequest) => {
	const moment = require("moment");

	// Determine origin value - backend may use 'origin' or 'location_origin'
	const originValue = searchRequest.origin || searchRequest.location_origin;
	// Determine destination value - backend may use 'destination' or 'location_destination'
	const destinationValue = searchRequest.destination || searchRequest.location_destination;

	// Build the filters object from the search request
	const filters = {
		...DEFAULT_HARD_FILTERS_VALUES,
		// Location filters - handle both 'origin' and 'location_origin' formats
		...(originValue && {
			location_origin: originValue,
			location_origin__view: searchRequest.location_origin__view || {
				value: originValue,
				label: originValue,
			},
		}),
		// Destination filters - handle both 'destination' and 'location_destination' formats
		...(destinationValue && {
			location_destination: destinationValue,
			location_destination__view: searchRequest.location_destination__view || {
				value: destinationValue,
				label: destinationValue,
			},
		}),
		...(searchRequest.destinationStates && {
			destinationStates: searchRequest.destinationStates,
			destinationStates__view: searchRequest.destinationStates__view,
		}),
		// Deadhead filters
		...(searchRequest.dh_origin && { dh_origin: searchRequest.dh_origin }),
		...(searchRequest.dh_destination && { dh_destination: searchRequest.dh_destination }),
		// Date filters - handle both 'availStart' and 'pickup_ds__after' formats
		...((searchRequest.availStart || searchRequest.pickup_ds__after) && {
			pickup_ds__after: searchRequest.pickup_ds__after || moment(searchRequest.availStart).valueOf(),
		}),
		...(searchRequest.pickup_ds__before && {
			pickup_ds__before: searchRequest.pickup_ds__before,
		}),
		// Equipment and truck specifications
		...(searchRequest.equipment && { equipment: searchRequest.equipment }),
		...(searchRequest.max_weight && { max_weight: searchRequest.max_weight }),
		...(searchRequest.trailer && { trailer: searchRequest.trailer }),
		...(searchRequest.load_type && { load_type: searchRequest.load_type }),
		// Carrier and truck
		...(searchRequest.carrier && {
			carrier: searchRequest.carrier,
			carrier__view: searchRequest.carrier__view || {
				value: searchRequest.carrier,
				label: "Carrier",
			},
		}),
		...(searchRequest.truck && {
			truck: searchRequest.truck,
			truck__view: searchRequest.truck__view || {
				value: searchRequest.truck,
				label: "Truck",
			},
		}),
		// Driver and blacklisted states
		...(searchRequest.driver && {
			driver: searchRequest.driver,
			driver__view: searchRequest.driver__view,
		}),
		...(searchRequest.blacklisted_states_ids && {
			blacklisted_states_ids: searchRequest.blacklisted_states_ids,
			blacklisted_states_ids__view: searchRequest.blacklisted_states_ids__view,
		}),
		// System filters
		...(searchRequest.system_show_relay_only !== undefined && {
			system_show_relay_only: searchRequest.system_show_relay_only,
		}),
		...(searchRequest.system_show_best_loads_only !== undefined && {
			system_show_best_loads_only: searchRequest.system_show_best_loads_only,
		}),
		...(searchRequest.system_hide_all_flexible !== undefined && {
			system_hide_all_flexible: searchRequest.system_hide_all_flexible,
		}),
	};

	// Build soft filters if available
	const softFilters = {
		...DEFAULT_SOFT_FILTERS_VALUES,
		// Add any soft filter preferences from the search request
		...(searchRequest.miles_preferred__max && { miles_preferred__max: searchRequest.miles_preferred__max }),
		...(searchRequest.miles_preferred__min && { miles_preferred__min: searchRequest.miles_preferred__min }),
		...(searchRequest.rate_per_mile_preferred && {
			rate_per_mile_preferred: searchRequest.rate_per_mile_preferred,
		}),
		...(searchRequest.rate_total_preferred && { rate_total_preferred: searchRequest.rate_total_preferred }),
		...(searchRequest.profit_preferred && { profit_preferred: searchRequest.profit_preferred }),
		...(searchRequest.extra_stops_preferred && { extra_stops_preferred: searchRequest.extra_stops_preferred }),
	};

	return {
		EMPTY: {
			filters,
			softFilters,
		},
	};
};

export const saveSearchesSessionStorage = async (searches) => {
	let searchesLocalStorage = {};
	for (const key in searches) {
		searchesLocalStorage[key] = {
			filters: searches[key].filters,
			softFilters: searches[key].softFilters,
			order: searches[key].order,
		};
	}
	if (searchesLocalStorage) sessionStorage.setItem("SEARCH_LIST", JSON.stringify(searchesLocalStorage));
};
